/**
 * /billing/checkout — in-app-checkout Phase 3 Session 3.7 rework.
 *
 * **Session 3.7 changes** (retire portal/polling/redirect):
 *   - `?session_id=` branch (`<CheckoutSuccessPolling>`) RETIRED — the hosted
 *     Stripe Checkout redirect flow is gone; `<InAppCheckout>` handles payment
 *     confirmation synchronously in-app.
 *   - `<CheckoutTierGrid>` → tier chosen → `<InAppCheckout>` (view machine).
 *   - On `<InAppCheckout>` `onSuccess`: navigate to
 *     `/builder?conversation=<id>` (when deep-linked) OR `/cockpit`.
 *   - `loadStripe` singleton initialised at module level (VITE_STRIPE_PUBLISHABLE_KEY).
 *   - Contact-only / Bespoke tier CTA fires in-app `<BespokeAwaiting>` flow
 *     instead of a mailto (FIX 5 — Phase 3 /review BLOCKING finding).
 *
 * Original file: signup-billing-mvp Phase 5 Session 7 (2026-05-13).
 *
 * Route registered programmatically in `apps/web/src/App.tsx` (this codebase
 * uses `createRoute` + a route tree, NOT `createFileRoute` — see
 * `apps/web/CLAUDE.md` "DO NOT use createFileRoute(...)").
 *
 * The `session_id` field is kept in the search schema as `.optional()` so
 * legacy bookmarks / back-compat Stripe redirects don't throw a validation
 * error — the field is simply ignored (no branch renders for it).
 */

import { useState } from 'react';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { CheckoutTierCard } from '@/components/billing/CheckoutTierCard';
import { InAppCheckout } from '@/components/billing/InAppCheckout';
import { BespokeAwaiting } from '@/components/billing/BespokeAwaiting';
import { SUPPORT_EMAIL, supportMailto } from '@/lib/support';

// ---------------------------------------------------------------------------
// Stripe singleton — loaded once at module level.
// `VITE_STRIPE_PUBLISHABLE_KEY` is injected at build time by deploy.yml /
// ci.yml. In dev it falls back to '' (loadStripe returns null — InAppCheckout
// shows an error state). This is a public client key (pk_test_* / pk_live_*);
// it is safe to embed in the bundle.
// ---------------------------------------------------------------------------
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? ''
);

/**
 * URL search-param schema. `session_id` is kept for back-compat (legacy
 * Stripe redirect bookmarks) but is IGNORED — the polling flow is retired.
 * `conversation` is threaded through the tier grid and into `<InAppCheckout>`
 * so post-subscribe navigation can return to a shared deal.
 */
export const billingCheckoutSearchSchema = z.object({
  session_id: z.string().optional(),
  conversation: z.string().optional(),
});

export type BillingCheckoutSearch = z.infer<typeof billingCheckoutSearchSchema>;

interface TierResponseRow {
  id: string;
  display_name: string;
  tagline: string;
  monthly_price_usd: number | null;
  feature_bullets: string[];
  is_active: boolean;
  sort_order?: number;
  contact_only?: boolean;
}

interface TiersEnvelope {
  data: TierResponseRow[];
}

export function BillingCheckoutPage() {
  const { conversation } = useSearch({ from: '/billing/checkout' });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stripe, setStripe] = useState<
    import('@stripe/stripe-js').Stripe | null
  >(null);
  const [selectedTier, setSelectedTier] = useState<TierResponseRow | null>(
    null
  );
  // Bespoke view machine: when true the page body is replaced by <BespokeAwaiting>.
  const [showBespoke, setShowBespoke] = useState(false);
  const [bespokeTier, setBespokeTier] = useState<TierResponseRow | null>(null);

  // Resolve the Stripe singleton on mount (it's a promise).
  // Using a separate useEffect-style via the promise is not needed — we store
  // the resolved instance in state so the InAppCheckout receives a real Stripe
  // object (not a promise). stripePromise is module-level so it resolves once.
  stripePromise.then((s) => {
    if (s && !stripe) setStripe(s);
  });

  // Post-subscribe navigation: if a conversation deep-link was carried, return
  // to the shared deal; otherwise land on the cockpit.
  function handleSuccess() {
    if (conversation) {
      void navigate({
        to: '/builder',
        search: { conversation, tab: 'workbench' as const },
      });
    } else {
      void navigate({ to: '/cockpit' });
    }
  }

  // Bespoke CTA handler: transition to the in-app BespokeAwaiting flow.
  function handleRequestBespoke(tier: TierResponseRow) {
    setBespokeTier(tier);
    setShowBespoke(true);
  }

  // ── Bespoke-awaiting view machine ──────────────────────────────────────────
  if (showBespoke) {
    return (
      <div className="min-h-screen bg-void font-body text-text px-6 py-6 md:py-8">
        <div className="max-w-[640px] mx-auto w-full">
          <BespokeAwaiting
            userId={user?.id}
            onDone={() => {
              setShowBespoke(false);
              setBespokeTier(null);
              // Navigate to cockpit after bespoke request is done.
              void navigate({ to: '/cockpit' });
            }}
          />
        </div>
      </div>
    );
  }

  // When a tier is chosen, transition to the in-app checkout view.
  if (selectedTier) {
    const tierPrice =
      selectedTier.monthly_price_usd !== null
        ? `$${selectedTier.monthly_price_usd} / mo`
        : '';

    return (
      <div className="min-h-screen bg-void font-body text-text px-6 py-6 md:py-8">
        <div className="max-w-[640px] mx-auto w-full">
          <InAppCheckout
            tierId={selectedTier.id}
            tierName={selectedTier.display_name}
            tierPrice={tierPrice}
            conversation={conversation}
            onSuccess={handleSuccess}
            onBack={() => setSelectedTier(null)}
            stripe={stripe}
          />
        </div>
      </div>
    );
  }

  return (
    <CheckoutTierGrid
      conversation={conversation}
      onChooseTier={setSelectedTier}
      onRequestBespoke={handleRequestBespoke}
    />
  );
}

interface CheckoutTierGridProps {
  conversation?: string;
  onChooseTier: (tier: TierResponseRow) => void;
  onRequestBespoke: (tier: TierResponseRow) => void;
}

function CheckoutTierGrid({
  conversation: _conversation,
  onChooseTier,
  onRequestBespoke,
}: CheckoutTierGridProps) {
  const query = useQuery<TierResponseRow[]>({
    queryKey: queryKeys.billing.tiers(),
    queryFn: async () => {
      const envelope = await api.get<TiersEnvelope>('/api/billing/tiers');
      return envelope.data;
    },
    staleTime: 60_000,
  });

  return (
    <div className="min-h-screen bg-void font-body text-text px-6 pt-4 pb-2">
      <div className="max-w-[1240px] mx-auto w-full">
        {/* Eyebrow — PricingPage L184-191 (mb tightened for 1366×768 single-screen fit) */}
        <div className="text-center font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-accent mb-2">
          Membership
        </div>

        {/* Serif headline — PricingPage L193-209. Merged H1+H2 into single
            element w/ inline italic accent span (2026-05-14) — reference renders
            two lines, but on authed checkout we collapse to one for viewport-fit. */}
        <h1
          className="text-center font-serif text-[26px] md:text-[30px] font-medium leading-[1.15] tracking-[-0.02em] text-text-bright mb-3"
          data-testid="checkout-headline"
        >
          Underwriting at the speed of thought.{' '}
          <span className="italic text-accent">Choose your tier.</span>
        </h1>

        {/* Blurb — PricingPage L211-220 (tightened size+mb for 1366×768 single-screen fit) */}
        <p className="text-center font-body text-[15px] leading-[1.55] text-text-muted max-w-[640px] mx-auto mb-4">
          AI-powered underwriting infrastructure that turns months of due
          diligence into minutes. Choose the tier that matches your deal flow.
        </p>

        {/* Tier row — 4 states (loading skeleton / error / empty / success).
            PricingPage L222-245 — flex, gap-6, justify-center. */}
        {query.isLoading && (
          <div
            className="flex flex-col md:flex-row gap-6 justify-center items-stretch"
            data-testid="checkout-tiers-loading"
          >
            {[0, 1].map((i) => (
              <div
                key={i}
                className="flex-1 max-w-[400px] min-w-0 h-[520px] bg-panel-bg border border-panel-border rounded-[3px] animate-pulse"
              />
            ))}
          </div>
        )}

        {query.isError && (
          <div
            className="max-w-md mx-auto text-center px-6 py-8 border border-danger/15 bg-danger-dim rounded-[3px]"
            data-testid="checkout-tiers-error"
          >
            <div className="font-mono text-xs uppercase tracking-[0.18em] text-danger mb-2">
              Could not load tiers
            </div>
            <p className="font-body text-sm text-text-muted mb-4">
              We hit an error fetching the membership tiers. Try refreshing the
              page.
            </p>
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="px-5 py-2.5 bg-panel-bg border border-panel-border rounded-[3px] font-mono text-xs uppercase tracking-[0.14em] text-text-muted hover:text-text-bright"
            >
              Retry
            </button>
          </div>
        )}

        {query.isSuccess && query.data.length === 0 && (
          <div
            className="max-w-md mx-auto text-center px-6 py-12 border border-panel-border bg-panel-bg rounded-[3px]"
            data-testid="checkout-tiers-empty"
          >
            <div className="font-mono text-xs uppercase tracking-[0.18em] text-text-muted mb-2">
              No tiers available
            </div>
            <p className="font-body text-sm text-text-muted">
              Membership tiers haven&rsquo;t been configured yet. Please reach
              out at{' '}
              <a
                href={supportMailto({
                  subject: 'Membership tiers not configured',
                })}
                className="text-accent hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </div>
        )}

        {query.isSuccess && query.data.length > 0 && (
          <div
            className="flex flex-col md:flex-row gap-6 justify-center items-stretch"
            data-testid="checkout-tiers-grid"
          >
            {query.data.map((tier, idx) => (
              <CheckoutTierCard
                key={tier.id}
                tier={tier}
                recommended={idx === 0}
                onChoose={onChooseTier}
                onRequestBespoke={onRequestBespoke}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
